import { inject, Injectable, signal } from '@angular/core';

import { ClientQuota, ClientQuotaItemData, ClientQuotaKey } from '../types/quota.type';
import { quotaCalculations, transformApiToClientQuota } from '../utils/quota.utils';
import { QuotaApiService } from './quota.api.service';

@Injectable({ providedIn: 'root' })
export class QuotaService {
	private readonly api = inject(QuotaApiService);

	public readonly $quota = signal<ClientQuota | null>(null);
	public readonly $loading = signal(false);

	private _reqId = 0;

	public async load(force = false): Promise<void> {
		const reqId = ++this._reqId;

		if (!force && this.$loading()) {
			return;
		}

		try {
			this.$loading.set(true);
			const response = await this.api.getQuota();

			if (this._reqId === reqId) {
				const quota = transformApiToClientQuota(response);
				this.$quota.set(quota);
			}
		} catch (error) {
			console.error(error);
		} finally {
			if (this._reqId === reqId) {
				this.$loading.set(false);
			}
		}
	}

	public setAllQuotas(quota: ClientQuota | null): void {
		this.$quota.set(quota);
	}

	public updateQuota<KEY extends ClientQuotaKey>(
		key: KEY,
		cb: (prev: ClientQuotaItemData<KEY>) => Pick<ClientQuotaItemData<KEY>, 'used'>
	): void {
		this.$quota.update((prev) => {
			try {
				if (!prev) {
					return prev;
				}

				const data = prev.standard[key].data as ClientQuotaItemData<KEY>;
				const next = cb(data);

				return {
					...prev,
					standard: {
						...prev.standard,
						[key]: {
							...prev.standard[key],
							data: {
								...prev.standard[key].data,
								...next,
								...quotaCalculations({
									total: data.total,
									used: next.used,
								}),
							},
						},
					},
				};
			} catch (error) {
				console.error(error);
				return prev;
			}
		});
	}
}
